import React, { useEffect, useMemo, useRef } from "react";

import {
  UseMutateAsyncFunction,
  UseMutateFunction,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { debounce, isEmpty, uniq } from "lodash";
import { observer } from "mobx-react-lite";
import { FieldError, useFieldArray, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import { FlowVersion, Schema } from "src/api/flowTypes";
import { SchemaUpdate } from "src/api/flowVersionQueries";
import { LOCK_SCHEMA_EDITOR_CLASSNAME } from "src/authoringMultiplayerLock/constants";
import { useLockableChildFocused } from "src/authoringMultiplayerLock/useLockableChildFocused";
import { useAcquireVersionResourceLock } from "src/authoringMultiplayerLock/useVersionResourceLock";
import { ResourceType } from "src/clients/flow-api";
import { useEntitySchemasJsonSchema } from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { useFeatures } from "src/featureCatalogue/queries";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { SchemaOptions, URLKeys } from "src/router/SearchParams";
import { CaseSchemaEditor } from "src/schema/schemaEditor/CaseSchemaEditor";
import { CustomSchemaEditor } from "src/schema/schemaEditor/CustomSchemaEditor";
import { EntitySchemaEditor } from "src/schema/schemaEditor/EntitySchemaEditor";
import { EventSchemaEditor } from "src/schema/schemaEditor/EventSchemaEditor";
import {
  SchemaToggles,
  SchemaViewType,
} from "src/schema/schemaEditor/SchemaToggles";
import { detectSchema } from "src/schema/schemaEditor/utils";
import {
  buildSubSchemas,
  PropertyUI,
  SchemaConverter,
  SchemaUI,
} from "src/schema/schemaMappingUtils";
import { UPDATE_DEBOUNCE_WAIT_TIME } from "src/store/GraphStore";
import { useSubmitForm } from "src/utils/useSubmitForm";

type Props = {
  schema: Schema;
  setSchemaInvalid: (errored: boolean) => void;
  type: SchemaOptions;
  flowVersionId: string;
  immutable: boolean;
  mutateSchema: UseMutateFunction<
    FlowVersion,
    AxiosError,
    SchemaUpdate,
    unknown
  >;
  mutateSchemaAsync: UseMutateAsyncFunction<
    FlowVersion,
    AxiosError,
    SchemaUpdate,
    unknown
  >;
  isOutputSchemaConfigured?: boolean;
  isSchemaMutating?: boolean;
};

export const SchemaEditor: React.FC<Props> = observer(
  ({
    schema,
    setSchemaInvalid,
    type,
    flowVersionId,
    immutable,
    mutateSchema,
    mutateSchemaAsync,
    isOutputSchemaConfigured = false,
    isSchemaMutating = false,
  }) => {
    const { workspace } = useWorkspaceContext();
    const featureGates = useWorkspaceFeatureGates();
    const isShowingInputSchema = type === "input";
    const editorHasLockableFocus = useLockableChildFocused({
      considerMarkerClass: LOCK_SCHEMA_EDITOR_CLASSNAME,
    });
    const previousValuesRef = useRef<SchemaUI | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const { data: entitySchemas } = useEntitySchemasJsonSchema({
      baseUrl: workspace.base_url!,
      options: {
        enabled: featureGates.entitiesEnabled,
      },
    });
    const { data: eventSchemas } = useEventSchemas();

    const { lockedByOtherUser, lockAcquired } = useAcquireVersionResourceLock(
      ResourceType.SCHEMA,
      flowVersionId,
      !immutable && editorHasLockableFocus,
    );

    const { data: features } = useFeatures({
      options: {
        enabled: featureGates.featuresEventsEnabled,
      },
    });

    const isReadonly = immutable || lockedByOtherUser;
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const isReactive =
      (!editorHasLockableFocus && !lockAcquired) || lockedByOtherUser;

    const { isEnabled: isCaseManagementModuleEnabled } =
      useIsCaseManagementEnabled();

    const convertedSchema = useMemo(
      () => SchemaConverter.beToUI(schema, type),
      [schema, type],
    );
    const formMethods = useForm<SchemaUI>({
      mode: "onChange",
      shouldFocusError: false,
      defaultValues: convertedSchema,
      ...(isReactive && {
        values: convertedSchema,
      }),
      resolver: (formValues: SchemaUI) => {
        const errors = {} as Record<string, FieldError>;
        const properties = formValues.properties;
        const propertyNames = properties.map((p: PropertyUI) => p.fieldName);

        // Validate that no field name is empty
        const emptyIndex = propertyNames.indexOf("");
        if (emptyIndex > -1) {
          errors[`properties.${emptyIndex}.fieldName`] = {
            type: "required",
          };

          return {
            values: {},
            errors,
          } as const;
        }

        // Validate that no field name contains a quote or a backslash or a dot
        propertyNames.forEach((name, index) => {
          if (!name.match(/^[^\\".]*$/)) {
            errors[`properties.${index}.fieldName`] = {
              type: "pattern",
            };
          }
        });

        // validate unique property names
        if (uniq(propertyNames).length !== propertyNames.length) {
          const duplicateNames = propertyNames.filter(
            (name, index) => propertyNames.indexOf(name) !== index,
          );
          const lastIndex = properties.findLastIndex(
            (p: PropertyUI) => p.fieldName === duplicateNames[0],
          );

          return {
            values: {} as const,
            errors: {
              [`properties.${lastIndex}.fieldName`]: {
                type: "unique",
              },
            } as const,
          };
        }

        return {
          values: Object.keys(errors).length > 0 ? {} : formValues,
          errors: errors,
        };
      },
    });

    const {
      formState: { errors },
      watch,
      reset,
    } = formMethods;
    const currentProperties = watch("properties");

    const mutateSchemaDebounce = useMemo(() => {
      const updateBackendSchema = async (
        updatedSchema: SchemaUI,
        type: SchemaOptions,
      ) => {
        const subSchemas = buildSubSchemas(
          updatedSchema,
          eventSchemas ?? [],
          entitySchemas ?? {},
          features ?? [],
        );
        const backendSchema = SchemaConverter.uiToBE(
          {
            $schema: updatedSchema.$schema,
            type: updatedSchema.type,
            properties: updatedSchema.properties,
            $defs: updatedSchema.$defs,
            definitions: updatedSchema.definitions,
          },
          type,
        );

        mutateSchema({
          type,
          schema: {
            ...backendSchema,
            $defs: {
              ...backendSchema.$defs,
              ...subSchemas,
            },
          },
        });
      };
      return debounce(updateBackendSchema, UPDATE_DEBOUNCE_WAIT_TIME);
    }, [eventSchemas, entitySchemas, features, mutateSchema]);

    const urlActiveView = searchParams.get(
      URLKeys.InputSchema,
    ) as SchemaViewType;

    const schemaToDisplay = useMemo(
      () =>
        detectSchema(
          currentProperties,
          isOutputSchemaConfigured,
          featureGates,
          isCaseManagementModuleEnabled,
          schema,
        ),
      [
        currentProperties,
        isOutputSchemaConfigured,
        featureGates,
        isCaseManagementModuleEnabled,
        schema,
      ],
    );

    const isCaseViewInvalid =
      urlActiveView === SchemaViewType.CASE && isOutputSchemaConfigured;

    const activeView = urlActiveView ?? schemaToDisplay;

    useEffect(() => {
      if (
        isCaseViewInvalid ||
        (currentProperties.length === 0 && !urlActiveView)
      ) {
        setSearchParams((prev) => {
          prev.set(URLKeys.InputSchema, SchemaViewType.CUSTOM);
          return prev;
        });
      }
    }, [isCaseViewInvalid, setSearchParams, urlActiveView, currentProperties]);

    useSubmitForm({
      onChange: async (newValues) => {
        // We want to filter out the fields with empty names here
        const filteredNewValues = {
          ...newValues,
          properties: newValues.properties.filter(
            (p: PropertyUI) => p?.fieldName !== "",
          ),
        };

        const previousProperties = previousValuesRef.current?.properties;
        // If the new values have properties,
        // or it used to have and now it is empty, then mutate the schema
        if (
          filteredNewValues.properties.length > 0 ||
          !previousProperties ||
          previousProperties.length > 0
        ) {
          mutateSchemaDebounce(filteredNewValues, type);
        }

        previousValuesRef.current = filteredNewValues;
      },
      disabled: isReactive,
      watch: watch,
    });

    useEffect(() => {
      if (lockedByOtherUser) {
        // Cancel pending invocations of schema updates and reset the form
        // in case another user acquires a lock while editing the form.
        reset();
        mutateSchemaDebounce.cancel();
      } else {
        // Ensure debounced mutation is instantly performed when the schema editor is dismounted.
        return () => {
          mutateSchemaDebounce.flush();
        };
      }
    }, [lockedByOtherUser, mutateSchemaDebounce, reset]);

    useEffect(() => {
      setSchemaInvalid(!isEmpty(errors));
    }, [errors, errors.properties, setSchemaInvalid]);

    // This hook cannot be moved into a child componenent because it causes a weird re-setting of ids property
    // ids that messes with the diff highlighting. Let's discuss other options with the team in case we need to move it.
    const propertiesFieldArrayReturn = useFieldArray({
      control: formMethods.control,
      name: "properties",
    });

    const renderContent = () => {
      if (activeView === SchemaViewType.EVENT && isShowingInputSchema) {
        return (
          <EventSchemaEditor
            formMethods={formMethods}
            isReadonly={isReadonly}
          />
        );
      }

      if (activeView === SchemaViewType.ENTITY && isShowingInputSchema) {
        return (
          <EntitySchemaEditor
            formMethods={formMethods}
            isReadonly={isReadonly}
          />
        );
      }

      if (
        activeView === SchemaViewType.CASE &&
        isShowingInputSchema &&
        isCaseManagementModuleEnabled
      ) {
        return (
          <CaseSchemaEditor
            formMethods={formMethods}
            isReadonly={isReadonly}
            mutateSchemaAsync={mutateSchemaAsync}
          />
        );
      }
      return (
        <CustomSchemaEditor
          formMethods={formMethods}
          isReadonly={isReadonly}
          isShowingInputSchema={isShowingInputSchema}
          propertiesFieldArrayReturn={propertiesFieldArrayReturn}
          scrollContainerRef={scrollContainerRef}
          type={type}
        />
      );
    };

    return (
      <div
        ref={scrollContainerRef}
        className="decideScrollbar h-full overflow-y-auto px-4 pt-5"
      >
        {isShowingInputSchema &&
          (featureGates.entitiesEnabled ||
            featureGates.featuresEventsEnabled ||
            isCaseManagementModuleEnabled) && (
            <SchemaToggles
              activeView={activeView}
              formMethods={formMethods}
              isOutputSchemaConfigured={isOutputSchemaConfigured}
              isReadonly={isReadonly}
              isSchemaMutating={isSchemaMutating}
              schema={schema}
              setActiveView={(view: SchemaViewType) => {
                setSearchParams((prev) => {
                  prev.set(URLKeys.InputSchema, view);
                  return prev;
                });
              }}
            />
          )}
        <div className={LOCK_SCHEMA_EDITOR_CLASSNAME}>{renderContent()}</div>
      </div>
    );
  },
);
